import { useRouter } from 'next/router';
import { z } from 'zod';

import {
  ConveyancingSituation,
  MortgageType,
  PriorityType,
  useCreateConveyancingRecommendationMutation,
} from '@graphql/requests';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { SubmitFormHandler } from '@ui-furniture/common/form';
import { PATHS } from '@ui-furniture/constants/paths';
import { useEnsureUserHasProfileAndIsAuthenticated } from '@ui-furniture/hooks/use-create-visitor-profile';
import { useLoadingCallback } from '@ui-furniture/hooks/use-loading-callback';

import { PurchaseSchema, SaleSchema } from '../common/quote-schema';

const BothSchema = PurchaseSchema.merge(SaleSchema);
type BothType = z.infer<typeof BothSchema>;

const getMortgageType = ({
  mortgage,
  additionalDetails,
}: BothType['purchaseDetails']): MortgageType | null => {
  if (!mortgage) return null;
  const buyToLet = additionalDetails?.find(({ value }) => value === 'buyToLet');
  if (buyToLet) return MortgageType.BuyToLet;
  return MortgageType.Residential;
};

export const useCreateConveyancingRecommendation = ({
  isSharedComponent = false,
} = {}) => {
  const { reportObjective } = useAnalyticsReportingService();
  const [createRecommendationMutation] =
    useCreateConveyancingRecommendationMutation();
  const { push } = useRouter();
  const ensureAuthenticated = useEnsureUserHasProfileAndIsAuthenticated();

  const [isSubmitting, submit] = useLoadingCallback<SubmitFormHandler<any>>(
    async ({
      situation,
      priority,
      urgency,
      purchaseDetails,
      saleDetails,
    }: BothType & {
      situation: ConveyancingSituation;
    }) => {
      await ensureAuthenticated();
      const result = await createRecommendationMutation({
        variables: {
          data: {
            situation,
            priority: priority ? priority.value : null,
            urgency: urgency ? urgency.value : null,
            purchaseDetails: purchaseDetails
              ? {
                  ...purchaseDetails,
                  additionalDetails: purchaseDetails.additionalDetails?.map(
                    ({ value }) => value
                  ),
                  tenure: purchaseDetails.tenure.value,
                  mortgage: getMortgageType(purchaseDetails),
                  areaOutcode: purchaseDetails.areaOutcode.value,
                }
              : undefined,
            saleDetails: saleDetails
              ? {
                  addressId: saleDetails.address.id,
                  hasMortgage:
                    situation === ConveyancingSituation.Remortgaging ||
                    saleDetails.mortgage,
                  price: saleDetails.price,
                  sellerCount: saleDetails.sellerCount,
                  additionalDetails: saleDetails.additionalDetails?.map(
                    ({ value }) => value
                  ),
                }
              : undefined,
          },
        },
      });
      const data = result.data?.conveyancingRecommendation;
      if (!data) throw new Error('Failed to create request');

      reportObjective({
        name: 'Conveyancing - Create Conveyancing Recommendation',
        category: 'Conveyancing',
        stage: 'event',
        isConversion: true,
        data,
      });

      let toPath = `/conveyancing/recommendation/view/${data.id}`;
      switch (data.priority) {
        case PriorityType.Service:
          toPath += '?order-by=rating&order-dir=desc';
          break;
        case PriorityType.Speed:
          toPath += '?order-by=accuracy&order-dir=asc';
          break;
        case PriorityType.Price:
        default:
          toPath += '?order-by=price&order-dir=asc';
          break;
      }

      if (!isSharedComponent) {
        await push(toPath);
      } else {
        window.parent.location.href = PATHS.webApp.createRoute.fullUrl(toPath);
      }

      return data;
    },
    [push, ensureAuthenticated]
  );

  return [isSubmitting, submit] as const;
};
